import {
  AbsolutesBlackColors,
  AbsolutesWhiteColors,
} from '@picsart/design-system/foundation/colors';
import { FontSizes, LineHeights } from '@picsart/design-system/foundation/typography';

import { FontWeights } from '@picsart/design-system/foundation/fonts';
import { CornerRadius } from '@picsart/design-system/foundation/cornerRadius';
import { Spacings } from '@picsart/design-system/foundation/spacings';
import { mediaQuery } from '@picsart/rc/helpers';
import { createUseStyles } from 'react-jss';
import { ColorSchemes } from 'redesignedComponents/context/colorScheme/enums';
import type { ColorSchemeType } from 'redesignedComponents/context/colorScheme/types';

const useStyles = createUseStyles<string, { colorScheme: ColorSchemeType }>(
  () => ({
    root: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
      padding: [Spacings.s64, Spacings.s32],
      margin: 'auto',
      width: '100%',
      // [mediaQuery(768)]: {
      '@media screen and (max-width: 768px)': {
        padding: Spacings.s0,
        overflow: 'hidden',
      },
      background: ({ colorScheme }) =>
        colorScheme === ColorSchemes.DARK
          ? AbsolutesBlackColors.base.default
          : AbsolutesWhiteColors.base.default,
      color: ({ colorScheme }) =>
        colorScheme === ColorSchemes.DARK
          ? AbsolutesWhiteColors.base.default
          : AbsolutesBlackColors.base.default,
    },
    toggleButtons: {
      margin: [Spacings.s32, Spacings.s0],
      // [mediaQuery(768)]: {
      '@media screen and (max-width: 768px)': {
        transform: 'translateX(24px)',
      },
    },
    description: {
      maxWidth: 730,
      marginBottom: Spacings.s32,
      fontSize: FontSizes.s20,
      fontWeight: FontWeights.medium,
      lineHeight: LineHeights.l28,
    },
    tabDescription: {
      marginTop: Spacings.s32,
      marginBottom: Spacings.s44,
      // [mediaQuery(768)]: {
      '@media screen and (max-width: 768px)': {
        margin: [Spacings.s0, Spacings.s0, Spacings.s24, Spacings.s0],
      },
    },
    largeDescription: {
      maxWidth: 800,
    },
    cta: {
      marginTop: Spacings.s2,
      marginBottom: Spacings.s16,
    },
    toggleButtonsSkeleton: {
      extend: 'toggleButtons',
      display: 'flex',
      width: 200,
      height: 60,
      '& > i': {
        borderRadius: CornerRadius.r24,
      },
    },
    skeletonDescription: {
      extend: 'description',
      height: 80,
      borderRadius: CornerRadius.r24,
    },
    skeletonCta: {
      extend: 'cta',
      height: 20,
      width: 200,
    },
    skeletonFlex: {
      display: 'flex',
      flexDirection: 'column',
      gap: 20,
      '& > i': {
        borderRadius: CornerRadius.r24,
      },
    },
  }),
  {
    name: 'use-cases',
  },
);

export const useTabStyles = createUseStyles<string, { colorScheme: ColorSchemeType }>(
  () => ({
    tab: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-around',
      gap: '1.3rem',
      margin: [Spacings.s48, 'auto'],
      width: '100%',
      // [mediaQuery(767)]: {
      '@media screen and (max-width: 767px)': {
        margin: [Spacings.s8, 'auto'],
        padding: 0,
        flexDirection: 'column',
      },
    },
    skeletonUseCaseTab: {
      extend: 'tab',
      margin: [Spacings.s0],
    },
    skeletonUseCaseTabList: {
      height: 300,
      width: '100%',
    },
    skeletonTitle: {
      height: 60,
      width: '40%',
      borderRadius: CornerRadius.r24,
    },
  }),
  {
    name: 'use-cases-tab',
  },
);

const IMAGE_DIMENSIONS = {
  width: 370,
  height: 284,
};

export const useTabImageStyles = createUseStyles<string, { colorScheme: ColorSchemeType }>(
  () => ({
    root: {
      width: '100%',
    },
    singleRoot: {
      width: '100%',
    },
    image: {
      position: 'static',
      [mediaQuery(1365)]: {
        aspectRatio: IMAGE_DIMENSIONS.width / IMAGE_DIMENSIONS.height,
      },
    },
    singleImage: {
      '&&': {
        height: 470,
      },
    },
    picture: {
      borderRadius: CornerRadius.r12,
      width: '100%',
    },
    caption: {
      marginTop: Spacings.s32,
      fontSize: FontSizes.s20,
      lineHeight: LineHeights.l28,
      fontWeight: FontWeights.semibold,
    },

    skeletonImage: {
      extend: 'image',
      height: IMAGE_DIMENSIONS.height,
      width: IMAGE_DIMENSIONS.width,
    },
  }),
  {
    name: 'use-cases-tab-image',
  },
);

export default useStyles;
